import React, { useCallback, useState } from 'react';
import { useLocation } from 'react-router';
import { FormikHelpers } from 'formik';
import i18n from '../../utilities/i18n';
import mediator from '../../utilities/mediator';
import { getAuthenticationError } from '../../utilities/auth';
import UpdatePassword, { UpdatePasswordFormValues } from './UpdatePassword';
import { useAppSelector } from '../../reducers/hooks';

/**
 * Handles both expired-password remediation and voluntary password changes.
 * Uses the authenticated user's email for the protected flow and navigation
 * state supplied by login for the expired-password flow.
 * @returns {React.JSX.Element}
 */
export default function ChangePassword() {
  const location = useLocation();
  const isLoggedIn = useAppSelector((state) => state.auth.loggedIn);
  const authenticatedEmail = useAppSelector((state) => state.user.email);
  const email = isLoggedIn ? authenticatedEmail : location.state?.email || '';
  const [errorMessage, setErrorMessage] = useState('');
  const [successModalOpen, setSuccessModalOpen] = useState(false);

  const onSubmit = useCallback(
    (
      values: UpdatePasswordFormValues,
      helpers: FormikHelpers<UpdatePasswordFormValues>,
    ) =>
      mediator
        .request('auth:changePassword', {
          data: {
            currentPassword: values.currentPassword,
            email,
            password: values.password,
          },
        })
        .then(() => {
          setErrorMessage('');
          helpers.resetForm();
          setSuccessModalOpen(true);
        })
        .catch((error) => {
          if (error.status === 403) {
            setErrorMessage(
              i18n.t('Auth_PasswordChange.errorInvalidCurrentPassword'),
            );
          } else if (error.status === 400) {
            setErrorMessage(
              getAuthenticationError(
                error.data,
                'Auth_PasswordChange.errorGeneric',
              ),
            );
          } else {
            setErrorMessage(
              getAuthenticationError(error, 'Auth_PasswordChange.errorGeneric'),
            );
          }
        }),
    [email],
  );

  return (
    <UpdatePassword
      title={i18n.t('Auth_PasswordChange.title')}
      description={i18n.t(
        isLoggedIn
          ? 'Auth_PasswordChange.descriptionAuthenticated'
          : 'Auth_PasswordChange.description',
      )}
      showCurrentPassword
      onSubmit={onSubmit}
      errorMessage={errorMessage}
      successModalOpen={successModalOpen}
      onSuccessConfirm={() => mediator.publishAndWait('user:loggedOut')}
    />
  );
}
